import { apiService } from "../api/apiAdapter";
import { isSalesforceEnabled } from "./featureFlagSalesforceUtils";
import { tgoa } from "../features/search/util";
import { addMonths } from "date-fns";
import toTitleCase from "./toTitleCase";
/**
 * Decide which app the user is trying to access.
 * This can be based on:
 * - subdomain
 * - query param
 * - localStorage
 * - feature flag
 */
export function resolveTargetApp() {
  // Example: ?app=mycart
  const params = new URLSearchParams(window.location.search);
  return params.get("app");
}

/**
 * Check whether the signed-in user has access to the app.
 * Returns subscription details if exists, null if not found or inactive
 */

/**
 * @param {string} email
 * @param {string} targetApp
 * @returns {Promise<{isValid: boolean, subscription: object|null, expiryDate: Date|null, targetApp: string}|null>}
 */
export const SUBSCRIPTION_MAP = {
  tg: "151",
  obesity: "2151",
  crush: "3151",
  aidh: "4151",
};
export const APP_ID_GROUPS = {
  tg: ["151", "168", "170"],
  obesity: ["2151", "2170"],
  crush: ["3151", "3170"],
  aidh: ["4151", "4170"],
};
export const TYPE_USER = "user";
export const TYPE_INSTITUTION = "institution";
export const BASE_URL = import.meta.env.REACT_APP_BASE_URL;

const escapeRegex = (value) => value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

const buildExactOriginPattern = (urlString) => {
  if (!urlString) return null;

  try {
    const { origin } = new URL(urlString);
    return new RegExp(`^${escapeRegex(origin)}\\/`, "i");
  } catch {
    return null;
  }
};

const INTERNAL_REFERRER_PATTERNS = [
  /^https:\/\/localhost:3000\//i,
  /^https:\/\/login\.test\.tg\.org\.au\//i,
  /^https:\/\/login\.tg\.org\.au\//i,
  /^https:\/\/.*\.b2clogin\.com\//i,
  /^https:\/\/.*\.app\.tg\.org\.au\//i,
  /^https:\/\/app\.tg\.org\.au\//i,
  /^https:\/\/tgldcdp\.tg\.org\.au\//i,
  /^https:\/\/app-sf\.tg\.org\.au\//i,
  /^https:\/\/tgldcdp-sf\.tg\.org\.au\//i,
  buildExactOriginPattern("https://login.ippon.tg.org.au/"),
  buildExactOriginPattern("https://login.test.tg.org.au/"),
  buildExactOriginPattern("https://login.tg.org.au/"),
  /^https:\/\/testtgsf\.tg\.org\.au\//i,
  buildExactOriginPattern(import.meta.env.VITE_TG_APP_BASE_URL),
  buildExactOriginPattern(import.meta.env.VITE_OBESID_APP_BASE_URL),
  buildExactOriginPattern(import.meta.env.VITE_DRTC_APP_BASE_URL),
].filter(Boolean);

export const isExcludedReferrer = (referrer = "") =>
  INTERNAL_REFERRER_PATTERNS.some((pattern) => pattern.test(referrer));

export const fetchInstitutionFromIP = async (referrer = "") => {
  const salesforceFlag = isSalesforceEnabled();
  const endpoint = salesforceFlag ? "SalesforceGetReqInfo" : "getReqInfo";
  // console.log(
  //   'Fetching institution info ',
  //   'BASE_URL',
  //   BASE_URL,
  //   'endpoint',
  //   endpoint,
  //   'isSalesforceEnabled()',
  //   salesforceFlag
  // );
  if (import.meta.env.VITE_MOCK_INSTITUTION === "true") {
    console.log("[VITE_MOCK_INSTITUTION] Returning fake institution");
    return {
      type: TYPE_INSTITUTION,
      name: "Test Institution",
      institutionName: "TestInst",
      shortName: "TestInst",
      id: "fake-institution-id",
      orgid: null,
      isReferral: false,
      subscriptions: [
        {
          tg_subscriptionid: "151",
          tg_expirydate: "2027-01-01",
          statecode: 1,
          statuscode: 1,
        },
        {
          tg_subscriptionid: "2151",
          tg_expirydate: "2027-01-01",
          statecode: 1,
          statuscode: 1,
        },
        {
          tg_subscriptionid: "3151",
          tg_expirydate: "2027-01-01",
          statecode: 1,
          statuscode: 1,
        },
        {
          tg_subscriptionid: "4151",
          tg_expirydate: "2027-01-01",
          statecode: 1,
          statuscode: 1,
        },
      ],
    };
  }

  if (!BASE_URL) {
    return null;
  }

  try {
    const res = await fetch(
      `${BASE_URL}/${endpoint}?r=${encodeURIComponent(referrer)}&t=${Date.now()}`,
    );

    if (!res.ok) {
      return null;
    }

    const data = await res.json();
    if (!data?.sub?.length) return null;
    return {
      type: TYPE_INSTITUTION,
      institutionName: data?.account?.name || data?.account?.tg_shortname || "Name Not found",
      subscriptions: data?.entitlement ?? [],
      isReferral: data?.accessContext?.source === "REFERRAL",
    };
  } catch (err) {
    console.warn("fetchInstitutionFromIP failed:", err);
    return null;
  }
};
export const getReferrer = () => {
  const referrer = document.referrer || "";
  const bootstrapReferrer = localStorage.getItem("bootstrapReferrer") || "";
  const savedReferrer = localStorage.getItem("savedReferrer") || "";

  if (bootstrapReferrer) {
    return bootstrapReferrer;
  }

  if (!referrer || isExcludedReferrer(referrer)) {
    return savedReferrer;
  }

  localStorage.setItem("savedReferrer", referrer);
  return referrer;
};

export const getInstitutionFromAccessKey = () => {
  const token = localStorage.getItem("tgt");
  if (!token) return null;

  try {
    const decoded = JSON.parse(atob(token));

    let expiresAt = null;
    if (decoded.exp) {
      expiresAt = decoded.exp > 1000000000000 ? decoded.exp : decoded.exp * 1000;
    } else {
      const storedExpiry = localStorage.getItem("tg_expirydate");
      if (storedExpiry) {
        const parsedExpiry = JSON.parse(atob(storedExpiry));
        const expiryDate = new Date(parsedExpiry);
        if (!Number.isNaN(expiryDate.getTime())) {
          expiresAt = expiryDate.getTime();
        }
      }
    }

    if (expiresAt && expiresAt < Date.now()) return null;

    const subscriptions = Array.isArray(decoded.subscriptions)
      ? decoded.subscriptions
      : Array.isArray(decoded.entitlement)
        ? decoded.entitlement
        : decoded.tg_subscriptionid
          ? [
              {
                tg_subscriptionid: String(decoded.tg_subscriptionid),
                tg_expirydate: decoded.exp,
              },
            ]
          : [];

    const institutionName = decoded.name || decoded.institution || decoded.institutionName || null;

    if (!institutionName) {
      return null;
    }

    return {
      type: TYPE_INSTITUTION,
      institution: institutionName,
      institutionName,
      id: decoded.id,
      orgid: decoded.orgid,
      shortName: decoded.shortName,
      isReferral: decoded.isReferral ?? true,
      subscriptions,
    };
  } catch {
    return null;
  }
};

export const getEmail = (claims) =>
  claims?.["signInNames.emailAddress"] || claims?.email || claims?.emails?.[0] || null;

export const parsedInstituteForUser = (claims) => {
  if (!claims) return null;
  if (claims.institution) return claims.institution;
  const email = getEmail(claims);
  if (!email) return null;
  return tgoa[`member@${email.split("@")[1]}`] || null;
};

export const authDetailsFromClaims = (claims) => {
  if (!claims) return null;
  if (claims.i) {
    return {
      type: TYPE_INSTITUTION,
      institution: claims.i.name,
      institutionName: claims.i.name,
    };
  }
  return {
    type: TYPE_USER,
    email: getEmail(claims),
    institution: parsedInstituteForUser(claims),
    institutionName: parsedInstituteForUser(claims),

    givenName: claims.name?.split(" ")[0] || null, // name is displayName in ADB2C it is required field
    familyName: claims.name?.split(" ").slice(1).join(" ") || null,
  };
};

export function hasActiveParentSubscription(subscriptions) {
  if (!subscriptions) return false;

  const subsArray = Array.isArray(subscriptions) ? subscriptions : [subscriptions];
  const now = new Date();

  return subsArray.some((sub) => {
    const isMultiuserSubscription = String(sub.tg_subscriptionid) === "168";
    const hasExplicitStatus = sub.statuscode != null || sub.statecode != null;
    const isActiveStatus = Number(sub.statuscode) === 1 && Number(sub.statecode) === 1;

    const expiry = sub?.tg_expirydate ? new Date(sub.tg_expirydate) : null;
    const isNotExpired = !expiry || expiry > now;

    return isMultiuserSubscription && isNotExpired && (!hasExplicitStatus || isActiveStatus);
  });
}

export function hasActiveParentSubscriptionSalesforce(subscriptions) {
  if (!subscriptions) return false;
  const subsArray = Array.isArray(subscriptions) ? subscriptions : [subscriptions];
  const now = new Date();
  return subsArray.some((sub) => {
    const isMultiuserSubscription = String(sub?.tg_subscriptionid) === "168";
    //const isParentMultiUser = !!sub?.isPrimaryContact;
    const hasExplicitStatus = sub?.statuscode != null || sub?.statecode != null; // need to adjust according to discussion How transition going to happen
    const isActiveStatus = Number(sub?.statuscode) === 1 && Number(sub?.statecode) === 1;
    const expiry = sub?.tg_expirydate ? new Date(sub.tg_expirydate) : null;
    const isNotExpired = !expiry || expiry > now; // need to adjust according to discussion How transition going to happen
    return (
      isMultiuserSubscription &&
      // isParentMultiUser &&
      isNotExpired &&
      (!hasExplicitStatus || isActiveStatus)
    );
  });
}

function hasMultiUserSubscription(profile) {
  const contact = profile?.data?.contact;
  const rawSubs = profile?.data?.subscriptions;

  const isChildUser = Boolean(contact?._tg_parentcontactid_value);
  const isSelfSubscribed = contact?.tg_flags === 9999;
  const isActiveParentSub = hasActiveParentSubscription(rawSubs);

  return isChildUser || isSelfSubscribed || isActiveParentSub;
}

function isContainChildUser(profile) {
  // contact parent true means MultiUser parent else child or normal user i.e false (if subscription type is 168 and for 151 its individual user)
  const isContactMultiuserParent = !!(
    profile?.data?.contact &&
    typeof profile.data.contact === "object" &&
    profile.data.contact.parentMultiUser
  );
  const rawSubs = profile?.data?.subscriptions;
  const subsArray = Array.isArray(rawSubs)
    ? rawSubs
    : rawSubs && typeof rawSubs === "object"
      ? [rawSubs]
      : [];
  const isSubscriprtionHaveMultiuser = subsArray.some((sub) => {
    return String(sub?.tg_subscriptionid) === "168";
  });
  return isSubscriprtionHaveMultiuser && !isContactMultiuserParent;
}

function hasMultiUserSubscriptionSalesforce(profile) {
  const rawSubs = profile?.data?.subscriptions;
  const isChildUser = isContainChildUser(profile);
  const subsArray = Array.isArray(rawSubs) ? rawSubs : [rawSubs];
  const isSelfSubscribed = subsArray.some((sub) => {
    return String(sub?.tg_subscriptionid) === "168" && sub?.tg_flags === 9999;
  });
  // Note tg_flags=91 not checking here because it means licence not included which is checking isActiveParentSub
  const isActiveParentSub = hasActiveParentSubscriptionSalesforce(rawSubs);

  return isChildUser || isSelfSubscribed || isActiveParentSub;
}
export async function checkSubscription(email, targetAppSlug) {
  if (!email)
    return {
      isValid: false,
      isMultiUser: false,
      allSubscriptions: [],
      error: "Missing email",
    };

  try {
    const profileResponse = await apiService.getUserProfile(email, true);
    if (!profileResponse?.success) {
      throw new Error(profileResponse?.message || "Failed to validate user subscription");
    }

    const rawSubscriptions = profileResponse?.data?.subscriptions;
    const subscriptions = Array.isArray(rawSubscriptions)
      ? rawSubscriptions
      : rawSubscriptions && typeof rawSubscriptions === "object"
        ? [rawSubscriptions]
        : [];

    const matched = validateSubscription(subscriptions, targetAppSlug);
    const isMultiUser = isSalesforceEnabled()
      ? hasMultiUserSubscriptionSalesforce(profileResponse)
      : hasMultiUserSubscription(profileResponse);

    if (matched && matched.tg_expirydate) {
      localStorage.setItem("tg_expirydate", btoa(matched.tg_expirydate));
    }

    return {
      isValid: !!matched,
      subscription: matched,
      isMultiUser,
      allSubscriptions: subscriptions,
      targetApp: targetAppSlug,
      expiryDate: matched?.tg_expirydate || null,
      subscriptionName:
        matched?.["tg_subscriptionid@OData.Community.Display.V1.FormattedValue"] || null,
      startDate: matched?.startdate || null,
      status: matched?.statuscode,
      state: matched?.statecode,
      error: matched ? null : "No matching active subscription",
    };
  } catch (err) {
    console.warn("checkSubscription error:", err);
    return {
      isValid: false,
      isMultiUser: false,
      allSubscriptions: [],
      error: err.message,
    };
  }
}

/**
 * Unified validator for any subscription record (IP or Personal)
 */
/**
 * Validates if a subscription list contains a specific ID that is active and not expired.
 */

export function allowLogin(expiry) {
  if (!expiry) return true;

  const today = new Date();

  if (Number.isNaN(expiry.getTime())) return false;

  const windowEnd = addMonths(expiry, 1);

  return today <= windowEnd;
}

export function validateSubscription(subscriptions, appSlug) {
  if (!Array.isArray(subscriptions)) return null;
  const validIds = APP_ID_GROUPS[appSlug] || [];

  return (
    subscriptions?.find((sub) => {
      const expiry = sub.tg_expirydate ? new Date(sub.tg_expirydate) : null;
      const hasExplicitStatus = sub.statuscode != null || sub.statecode != null;
      const isActiveStatus = Number(sub.statuscode) === 1 && Number(sub.statecode) === 1;

      return (
        validIds.includes(String(sub.tg_subscriptionid)) &&
        (!hasExplicitStatus || isActiveStatus) &&
        allowLogin(expiry, sub.tg_subscriptionid)
      );
    }) || null
  );
}

export function validateSubscriptionForAccess(subscriptions, appSlug) {
  if (!Array.isArray(subscriptions)) return null;
  const validIds = APP_ID_GROUPS[appSlug] || [];

  return (
    subscriptions?.find((sub) => {
      const expiry = sub.tg_expirydate ? new Date(sub.tg_expirydate) : null;
      const hasExplicitStatus = sub.statuscode != null || sub.statecode != null;
      const isActiveStatus = Number(sub.statuscode) === 1 && Number(sub.statecode) === 1;

      return (
        validIds.includes(String(sub.tg_subscriptionid)) &&
        (!hasExplicitStatus || isActiveStatus) &&
        allowLogin(expiry, sub.tg_subscriptionid)
      );
    }) || null
  );
}

export function isSubscriptionActiveNow(subscription, now = new Date()) {
  if (!subscription) return false;

  const hasExplicitStatus = subscription.statuscode != null || subscription.statecode != null;
  const isActiveStatus =
    Number(subscription.statuscode) === 1 && Number(subscription.statecode) === 1;
  const expiry = subscription.tg_expirydate ? new Date(subscription.tg_expirydate) : null;
  const isNotExpired = !expiry || expiry > now;

  return (!hasExplicitStatus || isActiveStatus) && isNotExpired;
}
/**
 * Initialise app-wide context (store, feature flags, etc.)
 */
export async function initialiseAppContext(app) {
  // Example side effects:
  // - set active app in store
  // - load feature flags
  // - configure API base URL
  console.info("Initialising app context for:", app);
}

export function getProfileName(authDetails, profileData) {
  if (!authDetails) {
    return "";
  }
  if (authDetails?.type === TYPE_INSTITUTION) {
    return toTitleCase(authDetails.institutionName);
  }

  const profileName =
    authDetails?.type === TYPE_USER && authDetails?.givenName
      ? `${authDetails.givenName} ${authDetails.familyName || ""}`.trim()
      : "";
  let contactProfileName = "";

  if (profileData?.contact) {
    contactProfileName = `${(
      profileData?.contact?.firstName || ""
    ).trim()} ${(profileData?.contact?.lastName || "").trim()}`.trim();
  }

  // If either givenName or familyName is 'unknown' (case-insensitive), fallback to contactProfileName if available
  if (
    authDetails?.type === TYPE_USER &&
    (authDetails?.givenName?.trim().toLowerCase() === "unknown" ||
      authDetails?.familyName?.trim().toLowerCase() === "unknown") &&
    contactProfileName
  ) {
    return toTitleCase(contactProfileName);
  }

  if (profileName?.toLowerCase() === "unknown" || !profileName) {
    return toTitleCase(contactProfileName);
  }

  return toTitleCase(profileName || contactProfileName || "");
}
